/**
 * SYS-B-01 (Phase 4 C1) — Numérotation des pièces comptables.
 */
import type { CodeJournal } from '@shared/types/Ecriture';

export type CompteurPieces = { dernier: number };
export type PieceExistante = { numero_piece?: string | null; tenant_id?: string };

const RE_PIECE = /^([A-Z]{2})-(\d{4})-(\d{5})$/;

export function formaterNumeroPiece(
  journal: CodeJournal | string,
  exercice: string,
  sequence: number,
): string {
  const j = String(journal || 'OD').trim().toUpperCase().slice(0, 2).padEnd(2, 'X');
  const exo = String(exercice).replace(/\D/g, '').slice(0, 4);
  if (!/^\d{4}$/.test(exo)) throw new Error(`Exercice invalide pour numérotation : ${exercice}`);
  if (!Number.isInteger(sequence) || sequence < 1) throw new Error(`Séquence pièce invalide : ${sequence}`);
  return `${j}-${exo}-${String(sequence).padStart(5, '0')}`;
}

export function parserNumeroPiece(
  numero: string | null | undefined,
): { journal: string; exercice: string; sequence: number } | null {
  if (!numero) return null;
  const m = RE_PIECE.exec(numero.trim().toUpperCase());
  if (!m) return null;
  return { journal: m[1]!, exercice: m[2]!, sequence: Number(m[3]) };
}

export function allouerNumeroPiece(
  compteur: CompteurPieces,
  journal: CodeJournal | string,
  exercice: string,
): { numero: string; compteur: CompteurPieces } {
  const suivant = (compteur.dernier || 0) + 1;
  return { numero: formaterNumeroPiece(journal, exercice, suivant), compteur: { dernier: suivant } };
}

export function verifierNumerotationPieces(
  pieces: readonly PieceExistante[],
  exercice: string,
  journal: CodeJournal | string = 'OD',
): { ok: boolean; trous: number[]; doublons: string[]; max: number } {
  const j = String(journal).trim().toUpperCase().slice(0, 2);
  const seqs: number[] = [];
  const seen = new Set<string>();
  const doublons: string[] = [];
  for (const p of pieces) {
    const parsed = parserNumeroPiece(p.numero_piece);
    if (!parsed || parsed.exercice !== exercice || parsed.journal !== j) continue;
    const key = p.numero_piece!.trim().toUpperCase();
    if (seen.has(key)) doublons.push(key);
    else seen.add(key);
    seqs.push(parsed.sequence);
  }
  seqs.sort((a, b) => a - b);
  const trous: number[] = [];
  const max = seqs.length ? seqs[seqs.length - 1]! : 0;
  let expect = 1;
  for (const s of seqs) {
    while (expect < s) { trous.push(expect); expect += 1; }
    expect = s + 1;
  }
  return { ok: trous.length === 0 && doublons.length === 0, trous, doublons, max };
}

export class SuppressionPieceInterditeError extends Error {
  constructor(public readonly numero_piece: string) {
    super(`Suppression physique interdite pour la pièce ${numero_piece}. Passez une extourne.`);
    this.name = 'SuppressionPieceInterditeError';
  }
}

export function assertPieceNonSupprimablePhysiquement(numero_piece: string | null | undefined): void {
  if (parserNumeroPiece(numero_piece)) throw new SuppressionPieceInterditeError(String(numero_piece));
}
